/**
 * Store Slice Abstraction
 *
 * Provides a library-agnostic way to create slices.
 * Currently wraps Redux Toolkit's createSlice, but can be swapped
 * for other implementations without changing consuming code.
 *
 * Slices should import createStoreSlice and PayloadAction from @/store/core
 * instead of @reduxjs/toolkit directly, so that the underlying library
 * can be replaced by updating this module alone.
 */

import {
  createSlice as createReduxSlice,
  type SliceCaseReducers,
  type CreateSliceOptions,
  type Slice,
} from '@reduxjs/toolkit';

/**
 * Create a store slice.
 *
 * Drop-in replacement for Redux Toolkit's createSlice that keeps the
 * application decoupled from the underlying state management library.
 * The full typed slice (with inferred action creators) is returned so
 * that all type safety is preserved.
 */
export function createStoreSlice<
  State,
  CaseReducers extends SliceCaseReducers<State>,
  Name extends string = string,
>(options: CreateSliceOptions<State, CaseReducers, Name>): Slice<State, CaseReducers, Name> {
  return createReduxSlice(options);
}

